/**
 * Estrela de favorito do imóvel (Figma, out/2026) — usada no card do
 * Dashboard, nos cards da lista de Imóveis e na tabela. Atualiza o Redux na
 * hora e grava na API (`PATCH /consumer-units/:id/favourite`); se falhar,
 * desfaz. Favoritos sobem para o topo das listas (`utils/consumerUnitOrder`).
 */
import React from 'react';
import styled from 'styled-components';
import { BsStar, BsStarFill } from 'react-icons/bs';

import { useAppDispatch } from 'store/hooks';
import { setConsumerUnitFavourite } from 'store/consumer_unit/consumer_unit.slice';
import { updateConsumerUnitFavouriteService } from 'services/consumer_unit.service';
import { alert } from 'lib/alert';

interface FavouriteStarProps {
  consumerUnitId: string;
  isFavourite?: boolean;
  /** Fundo branco redondo para ficar legível sobre a foto do imóvel. */
  onPhoto?: boolean;
  className?: string;
}

export const FavouriteStar = ({
  consumerUnitId,
  isFavourite = false,
  onPhoto = false,
  className,
}: FavouriteStarProps) => {
  const dispatch = useAppDispatch();

  const toggle = async (e: React.MouseEvent) => {
    // o card/linha também é clicável (abre o imóvel ou seleciona no Dashboard)
    e.stopPropagation();
    e.preventDefault();
    const next = !isFavourite;
    dispatch(
      setConsumerUnitFavourite({ id: consumerUnitId, isFavourite: next })
    );
    try {
      await updateConsumerUnitFavouriteService(consumerUnitId, next);
    } catch {
      dispatch(
        setConsumerUnitFavourite({ id: consumerUnitId, isFavourite: !next })
      );
      alert.error('Não foi possível atualizar o favorito.');
    }
  };

  const label = isFavourite ? 'Remover dos favoritos' : 'Marcar como favorito';

  return (
    <StarButton
      type="button"
      className={className}
      $onPhoto={onPhoto}
      onClick={toggle}
      aria-pressed={isFavourite}
      aria-label={label}
      title={label}
    >
      {isFavourite ? <BsStarFill /> : <BsStar />}
    </StarButton>
  );
};

const StarButton = styled.button<{ $onPhoto: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: ${(props) =>
    props.$onPhoto ? props.theme.color.bg.primary : 'transparent'};
  color: ${(props) => props.theme.color.text.tertiary};
  font-size: 16px;
  cursor: pointer;

  :hover {
    filter: brightness(1.2);
  }
`;
